/* ── SPCore — SmartPack USA theme core ── */
(function () {
  'use strict';

  const SPCore = (window.SPCore = window.SPCore || {});
  SPCore.settings = SPCore.settings || {};

  const BP =
    Array.isArray(SPCore.settings.breakpoints) && SPCore.settings.breakpoints.length
      ? SPCore.settings.breakpoints.slice().sort((a, b) => b - a)
      : [1300, 990, 750, 600, 430];

  /* ── config ── */
  SPCore.config = {
    breakpoints: BP,
    mediaQueryDown: (n) => 'screen and (max-width: ' + n + 'px)',
    mediaQueryUp: (n) => 'screen and (min-width: ' + (n + 1) + 'px)',
    breakpoint: '',
    motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    isRTL: document.documentElement.getAttribute('dir') === 'rtl',
    isTouch: (function () {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      return hasTouch && !window.matchMedia('(pointer: fine)').matches;
    })(),
    hasLocalStorage: (function () {
      try {
        const k = 'spcore:test';
        window.localStorage.setItem(k, '1');
        window.localStorage.removeItem(k);
        return true;
      } catch (e) {
        return false;
      }
    })(),
  };

  /* ── DOMready ── */
  SPCore.DOMready = function (callback) {
    document.readyState !== 'loading'
      ? callback()
      : document.addEventListener('DOMContentLoaded', callback, { once: true });
  };

  /* ── utils ── */
  SPCore.utils = {
    throttle: function (fn) {
      let rafId = null;
      let lastArgs;
      const run = (ctx) => () => {
        rafId = null;
        fn.apply(ctx, lastArgs);
      };
      const throttled = function (...args) {
        lastArgs = args;
        if (rafId === null) rafId = requestAnimationFrame(run(this));
      };
      throttled.cancel = () => {
        cancelAnimationFrame(rafId);
        rafId = null;
      };
      return throttled;
    },

    debounce: function (fn, wait = 0) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    setScrollbarWidth: function () {
      const w = window.innerWidth - document.body.clientWidth;
      if (w > 0) document.documentElement.style.setProperty('--scrollbar-width', w + 'px');
    },

    fetchConfig: function (type = 'json') {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/' + type },
      };
    },

    setStorage: function (key, value, ttl) {
      if (!SPCore.config.hasLocalStorage) return;
      const record = { value: value, expires: ttl ? Date.now() + ttl : null };
      try {
        window.localStorage.setItem(key, JSON.stringify(record));
      } catch (e) {}
    },
    getStorage: function (key) {
      if (!SPCore.config.hasLocalStorage) return null;
      try {
        const record = JSON.parse(window.localStorage.getItem(key));
        if (!record) return null;
        if (record.expires && Date.now() > record.expires) {
          window.localStorage.removeItem(key);
          return null;
        }
        return record.value;
      } catch (e) {
        return null;
      }
    },
  };

  /* ── a11y ── */
  SPCore.a11y = {
    _handlers: {},

    getFocusableElements: function (container) {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      );
    },

    trapFocus: function (container, elementToFocus = container) {
      const els = this.getFocusableElements(container);
      const first = els[0];
      const last = els[els.length - 1];

      this.removeTrapFocus();

      this._handlers.focusin = (e) => {
        if (e.target !== container && e.target !== last && e.target !== first) return;
        document.addEventListener('keydown', this._handlers.keydown);
      };
      this._handlers.focusout = () => {
        document.removeEventListener('keydown', this._handlers.keydown);
      };
      this._handlers.keydown = (e) => {
        if (e.code.toUpperCase() !== 'TAB') return;
        if (e.target === last && !e.shiftKey) {
          e.preventDefault();
          first.focus();
        }
        if ((e.target === container || e.target === first) && e.shiftKey) {
          e.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', this._handlers.focusout);
      document.addEventListener('focusin', this._handlers.focusin);
      if (elementToFocus) elementToFocus.focus();
    },

    removeTrapFocus: function (elementToFocus = null) {
      document.removeEventListener('focusin', this._handlers.focusin);
      document.removeEventListener('focusout', this._handlers.focusout);
      document.removeEventListener('keydown', this._handlers.keydown);
      if (elementToFocus) elementToFocus.focus();
    },
  };

  /* ── pubsub ── */
  SPCore.events = {
    cartUpdate: 'cart:update',
    cartError: 'cart:error',
    variantChange: 'variant:change',
    quantityUpdate: 'quantity:update',
    facetUpdate: 'facet:update',
    breakpointChange: 'breakpoint:change',
  };

  SPCore.pubsub = (function () {
    const subscribers = {};
    return {
      subscribe: function (event, callback) {
        (subscribers[event] = subscribers[event] || []).push(callback);
        return function unsubscribe() {
          subscribers[event] = (subscribers[event] || []).filter((c) => c !== callback);
        };
      },
      publish: function (event, data) {
        (subscribers[event] || []).forEach((cb) => cb(data));
      },
    };
  })();

  /* ── responsive ── */
  function getBreakpoint() {
    const w = window.innerWidth;
    if (w > BP[0]) return 'full';
    let tier = BP[0];
    for (let i = 0; i < BP.length; i++) if (w <= BP[i]) tier = BP[i];
    return String(tier);
  }
  function syncBreakpoint() {
    const bp = getBreakpoint();
    if (bp === SPCore.config.breakpoint) return;
    SPCore.config.breakpoint = bp;
    document.documentElement.dataset.bp = bp;
    SPCore.pubsub.publish(SPCore.events.breakpointChange, { breakpoint: bp, width: window.innerWidth });
  }

  /* ── preloader ── */
  SPCore.preloader = {
    init: function () {
      const root = document.documentElement;
      const el = document.querySelector('.sp-preloader');
      if (!el || root.classList.contains('sp-preloader-skip')) return;

      const start = window.SPPreloaderStart || Date.now();
      const min = parseInt(el.dataset.min, 10) || 600;
      const max = parseInt(el.dataset.max, 10) || 4000;

      let minPassed = false;
      let loaded = document.readyState === 'complete';
      let done = false;

      const finish = () => {
        if (done) return;
        done = true;
        root.classList.add('sp-preloader-done');
        try {
          window.sessionStorage.setItem('sp:preloaded', '1');
        } catch (e) {}
        const remove = () => {
          if (el.parentNode) el.parentNode.removeChild(el);
        };
        el.addEventListener('transitionend', remove, { once: true });
        setTimeout(remove, 800);
      };

      const maybeFinish = () => {
        if (minPassed && loaded) finish();
      };

      setTimeout(() => {
        minPassed = true;
        maybeFinish();
      }, Math.max(0, min - (Date.now() - start)));

      if (!loaded)
        window.addEventListener(
          'load',
          () => {
            loaded = true;
            maybeFinish();
          },
          { once: true }
        );

      setTimeout(finish, Math.max(0, max - (Date.now() - start)));
    },
  };

  /* ── page fade ── */
  SPCore.pageFade = {
    init: function () {
      const root = document.documentElement;
      const done = () => root.classList.add('sp-faded');
      document.body.addEventListener('animationend', (e) => {
        if (e.target === document.body && e.animationName === 'sp-page-fade-in') done();
      });
      const dur = parseFloat(getComputedStyle(root).getPropertyValue('--sp-page-transition-dur')) || 300;
      setTimeout(done, dur + 100);
    },
  };

  /* ── short alias ── */
  window.Core = SPCore;

  /* ── boot ── */
  SPCore.utils.setScrollbarWidth();
  syncBreakpoint();
  SPCore.preloader.init();
  SPCore.DOMready(SPCore.pageFade.init);
  window.addEventListener(
    'resize',
    SPCore.utils.throttle(function () {
      SPCore.utils.setScrollbarWidth();
      syncBreakpoint();
    })
  );
  document.documentElement.classList.add('spcore-ready');
})();
